// RosterPanel.js
// Shared mid-draft roster bottom-sheet. Two modes:
//   • Single team — pass `draftedPlayers` + `positions` (used by Draft.js, unchanged look).
//   • Versus (2-team toggle) — pass `teams` = [{ label, players, positions }, …]
//     (used by PickUpDraft.js so you can flip between YOUR roster and the opponent's).
//
// Extracted verbatim from the original Draft.js RosterPanel so the normal-draft sheet is
// pixel-identical; versus mode only adds a small segmented YOU/OPP toggle under the header.
// PlayerCard defaults to hiding ratings, so the opponent's ratings are not revealed mid-draft.

import React, { useState } from "react";
import PlayerCard from "./PlayerCard";
import { displayPosition } from "../managers/playerService";
import wordmark from "../assets/Rosterable.png";

export default function RosterPanel({ sport, onClose, draftedPlayers, positions, teams }) {
  const isVersus = Array.isArray(teams) && teams.length > 0;
  const [teamIdx, setTeamIdx] = useState(0);

  const active = isVersus
    ? teams[Math.min(teamIdx, teams.length - 1)]
    : { players: draftedPlayers || [], positions: positions || [] };

  return (
    <div style={styles.panelOverlay} onClick={onClose}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div style={styles.panelHandle} />
        <div style={styles.panelHeader}>
          <img src={wordmark} alt="Rosterable" style={{ height: 18 }} />
          <button style={styles.panelClose} onClick={onClose}>✕</button>
        </div>

        {isVersus && (
          <div style={styles.toggleRow}>
            {teams.map((t, i) => {
              const sel = i === teamIdx;
              return (
                <button
                  key={i}
                  onClick={() => setTeamIdx(i)}
                  style={{
                    ...styles.toggleBtn,
                    background: sel ? sport.hex : "transparent",
                    color: sel ? "#0c0d10" : "rgba(255,255,255,0.6)",
                    borderColor: sel ? sport.hex : "rgba(255,255,255,0.12)",
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        <div style={{ ...styles.panelDivider, background: sport.hex }} />
        <div style={styles.panelList}>
          {active.players.length === 0 ? (
            <p style={styles.panelEmpty}>No players drafted yet</p>
          ) : (
            active.players.map((player, i) => (
              <PlayerCard
                key={i}
                player={player}
                sport={sport}
                position={displayPosition(active.positions[i], player)}
                onTap={() => {}}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  panelOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "flex-end",
    zIndex: 100,
  },
  panel: {
    width: "100%",
    maxWidth: "480px",
    margin: "0 auto",
    background: "#0f0f1a",
    borderRadius: "20px 20px 0 0",
    padding: "0 20px 40px",
    maxHeight: "80vh",
    overflowY: "auto",
  },
  panelHandle: {
    width: "36px",
    height: "4px",
    borderRadius: "2px",
    background: "rgba(255,255,255,0.2)",
    margin: "12px auto 8px",
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    paddingBottom: "10px",
  },
  panelClose: {
    position: "absolute",
    right: 0,
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.5)",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  // Versus YOU/OPP segmented toggle (only rendered in versus mode).
  toggleRow: {
    display: "flex",
    gap: "8px",
    justifyContent: "center",
    paddingBottom: "12px",
  },
  toggleBtn: {
    flex: 1,
    maxWidth: "140px",
    padding: "7px 0",
    borderRadius: "9px",
    border: "1px solid",
    fontSize: "12px",
    fontWeight: "800",
    letterSpacing: "1px",
    cursor: "pointer",
    background: "transparent",
  },
  panelDivider: {
    height: "1px",
    opacity: 0.5,
    marginBottom: "12px",
  },
  panelList: { display: "flex", flexDirection: "column", gap: "8px" },
  panelEmpty: {
    textAlign: "center",
    color: "rgba(255,255,255,0.3)",
    fontSize: "13px",
    paddingTop: "32px",
  },
};
